// ===========================================
// Lesson Filters Component
// ===========================================

import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { LessonFilters } from '../../../api/lessons.api';
import { DAYS_OF_WEEK } from './lessons.constants';

interface Term {
  id: string;
  name: string;
  isActive: boolean;
}

interface LessonType {
  id: string;
  name: string;
  type: string;
}

interface Teacher {
  id: string;
  User: {
    firstName: string;
    lastName: string;
  };
}

interface Instrument {
  id: string;
  name: string;
}

interface LessonFiltersProps {
  filters: LessonFilters;
  onFiltersChange: (filters: LessonFilters) => void;
  terms: Term[];
  lessonTypes: LessonType[];
  teachers: Teacher[];
  instruments: Instrument[];
  showTeacherFilter: boolean;
}

export default function LessonFiltersPanel({
  filters,
  onFiltersChange,
  terms,
  lessonTypes,
  teachers,
  instruments,
  showTeacherFilter,
}: LessonFiltersProps) {
  return (
    <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Term</InputLabel>
        <Select
          value={filters.termId || ''}
          label="Term"
          onChange={(e) =>
            onFiltersChange({ ...filters, termId: e.target.value || undefined })
          }
        >
          <MenuItem value="">All Terms</MenuItem>
          {terms.map((term) => (
            <MenuItem key={term.id} value={term.id}>
              {term.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Type</InputLabel>
        <Select
          value={filters.lessonTypeId || ''}
          label="Type"
          onChange={(e) =>
            onFiltersChange({ ...filters, lessonTypeId: e.target.value || undefined })
          }
        >
          <MenuItem value="">All Types</MenuItem>
          {lessonTypes.map((lt) => (
            <MenuItem key={lt.id} value={lt.id}>
              {lt.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Day</InputLabel>
        <Select
          value={filters.dayOfWeek ?? ''}
          label="Day"
          onChange={(e) =>
            onFiltersChange({
              ...filters,
              dayOfWeek: e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
        >
          <MenuItem value="">All Days</MenuItem>
          {DAYS_OF_WEEK.map((day) => (
            <MenuItem key={day.value} value={day.value}>
              {day.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Instrument</InputLabel>
        <Select
          value={filters.instrumentId || ''}
          label="Instrument"
          onChange={(e) =>
            onFiltersChange({ ...filters, instrumentId: e.target.value || undefined })
          }
        >
          <MenuItem value="">All Instruments</MenuItem>
          {instruments.map((inst) => (
            <MenuItem key={inst.id} value={inst.id}>
              {inst.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {showTeacherFilter && (
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Teacher</InputLabel>
          <Select
            value={filters.teacherId || ''}
            label="Teacher"
            onChange={(e) =>
              onFiltersChange({ ...filters, teacherId: e.target.value || undefined })
            }
          >
            <MenuItem value="">All Teachers</MenuItem>
            {teachers.map((teacher) => (
              <MenuItem key={teacher.id} value={teacher.id}>
                {teacher.User.firstName} {teacher.User.lastName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  );
}
